import { action, computed, makeObservable, observable } from 'mobx';

import { DEFAULT_TABLE_SETTINGS_MODEL } from './consts';
import type { TrajectoryGraphStore } from './features/trajectory-graph/trajectory-graph.store';
import { DisplayManager } from './features/trajectory-table/display-manager';
import type { TrajectoryTableSettingsModel } from './types/host-services';
import type { TrajectoryExpandState, TrajectoryWidgetStateParams } from './types/state';

export class TrajectoryUiStore {
  @observable expandState: TrajectoryExpandState = 'default';
  @observable private _isTableSettingsOpen = false;
  @observable private _isTableCheckboxesVisible = false;
  @observable private _isTrajectoryListOpen = false;
  @observable activeSettingsTab: 'charts' | 'table' = 'table';
  /** `undefined` = uncontrolled; boolean = controlled from host props. Must be observable for MobX. */
  @observable private controlledTableSettingsOpen: boolean | undefined = undefined;
  @observable private controlledTableCheckboxesVisible: boolean | undefined = undefined;
  @observable private controlledTrajectoryListOpen: boolean | undefined = undefined;
  @observable.ref displayManager: DisplayManager;
  /** Portal root for settings Drawer; null/undefined → document.body. */
  @observable.ref settingsDrawerContainer: Element | null = null;

  private onStateChange?: (state: TrajectoryWidgetStateParams) => void;
  private graphStore: TrajectoryGraphStore | null = null;

  private onTableSettingsOpenChange?: (open: boolean) => void;
  private onTableCheckboxesVisibleChange?: (visible: boolean) => void;
  private onTrajectoryListOpenChange?: (open: boolean) => void;

  constructor(initialState?: TrajectoryWidgetStateParams) {
    this.expandState = initialState?.expandState ?? 'default';
    this.displayManager = new DisplayManager({ ...DEFAULT_TABLE_SETTINGS_MODEL, ...initialState?.tableSettings }, () =>
      this.emitStateChange(),
    );

    makeObservable(this);
  }

  @action.bound
  bindGraphStore(graphStore: TrajectoryGraphStore): void {
    this.graphStore = graphStore;
  }

  @action.bound
  setSettingsDrawerContainer(container: Element | null): void {
    this.settingsDrawerContainer = container;
  }

  @action.bound
  setExternalCallbacks(options: {
    onStateChange?: (state: TrajectoryWidgetStateParams) => void;
    isTableSettingsOpen?: boolean;
    isTableCheckboxesVisible?: boolean;
    isTrajectoryListOpen?: boolean;
    onTableSettingsOpenChange?: (open: boolean) => void;
    onTableCheckboxesVisibleChange?: (visible: boolean) => void;
    onTrajectoryListOpenChange?: (open: boolean) => void;
  }): void {
    this.onStateChange = options.onStateChange;
    this.controlledTableSettingsOpen = options.isTableSettingsOpen;
    this.controlledTableCheckboxesVisible = options.isTableCheckboxesVisible;
    this.controlledTrajectoryListOpen = options.isTrajectoryListOpen;
    this.onTableSettingsOpenChange = options.onTableSettingsOpenChange;
    this.onTableCheckboxesVisibleChange = options.onTableCheckboxesVisibleChange;
    this.onTrajectoryListOpenChange = options.onTrajectoryListOpenChange;
  }

  @computed
  get isTableSettingsOpen(): boolean {
    return this.controlledTableSettingsOpen ?? this._isTableSettingsOpen;
  }

  @computed
  get isTableCheckboxesVisible(): boolean {
    return this.controlledTableCheckboxesVisible ?? this._isTableCheckboxesVisible;
  }

  @computed
  get isTrajectoryListOpen(): boolean {
    return this.controlledTrajectoryListOpen ?? this._isTrajectoryListOpen;
  }

  @action.bound
  setExpandState(expandState: TrajectoryExpandState): void {
    this.expandState = expandState;
    this.emitStateChange();
  }

  @action.bound
  setTableSettingsOpen(open: boolean): void {
    if (this.controlledTableSettingsOpen === undefined) {
      this._isTableSettingsOpen = open;
    }

    this.onTableSettingsOpenChange?.(open);
  }

  @action.bound
  setTableCheckboxesVisible(visible: boolean): void {
    if (this.controlledTableCheckboxesVisible === undefined) {
      this._isTableCheckboxesVisible = visible;
    }

    this.onTableCheckboxesVisibleChange?.(visible);
  }

  @action.bound
  setTrajectoryListOpen(open: boolean): void {
    if (this.controlledTrajectoryListOpen === undefined) {
      this._isTrajectoryListOpen = open;
    }

    this.onTrajectoryListOpenChange?.(open);
  }

  @action.bound
  setActiveSettingsTab(tab: 'charts' | 'table'): void {
    this.activeSettingsTab = tab;
  }

  getSnapshot(): TrajectoryWidgetStateParams {
    return {
      expandState: this.expandState,
      chartDimension: this.graphStore?.chartDimension ?? '2d',
      trajectory3dViewControl: this.graphStore?.trajectory3dViewControl ?? null,
      chart2dSettings: this.graphStore?.chart2dSettings,
      tableSettings: { ...this.displayManager.settings } as TrajectoryTableSettingsModel,
    };
  }

  @action.bound
  emitStateChange(): void {
    this.onStateChange?.(this.getSnapshot());
  }

  /** Persist 3D camera into host snapshot on graph unmount (C2). */
  @action.bound
  flushCameraState(): void {
    this.emitStateChange();
  }

  @action.bound
  dispose(): void {
    this.onStateChange = undefined;
    this.graphStore = null;
  }
}
